/**
 * Motor de permisos — ÚNICA fuente de verdad del permiso efectivo.
 *
 * resolveEffective (spec 2026-10-07, solo empresa; reemplaza la cadena de
 * carpetas del 2026-10-06):
 *  1. Root (documents.root === true, o isRootUser): FULL_PERMS.
 *  2. Efectivo = la base de la EMPRESA: su matriz de empresa si existe (admin
 *     de empresa: ADMIN_PERMS con los overrides de su matriz); si no, el valor
 *     por defecto del rol (lib/role_seed defaultCompanyPerms: cliente →
 *     plantilla, admin → ADMIN_PERMS, personal global / con alcance / otro rol
 *     → permisos del rol). La empresa PUEDE quedar por debajo o por encima del
 *     rol: el rol ya no es piso.
 *  3. folders.closing lo decide solo el rol (admin: nunca), nunca una matriz.
 * Vale igual en TODA carpeta de la empresa. Lo que se le oculta a un usuario
 * lo decide lib/folder_restrictions (restricciones, spec §3), no este motor.
 *
 * Errores de lectura (ruling R2): `companyMatrixState: 'error'` da el
 * efectivo VACÍO (todo en false). Jamás se cae en los permisos por defecto
 * del rol porque una lectura falló. `companyMatrixState: 'present'` sin
 * `companyMatrix` (doc de empresa sin `permissions`) también da base vacía,
 * no el defecto. Sin estado (o 'missing') y `companyMatrix` null = no hay doc
 * → defecto del rol.
 *
 * computeEffective es el motor ANTERIOR (unión con el rol como piso), LEGADO.
 * Queda para tests y para la comparación de scripts/compare_effective.js
 * (vía su copia CJS); la UI no lo usa.
 *
 * COPIA CJS en daysi-cloud-functions/scripts/effective_engine_lib.js;
 * effective_sync.test.js corre la misma tabla en las dos.
 */
import { mergePermissions } from './permission_merge';
import { legacyLinkedRaw, INHERIT, GRANULAR_DOC_FLAGS } from './permission_granular';
import { userKind, defaultCompanyPerms } from './role_seed';

const NEW_FLAGS = { createXml: true, createLink: true, createReference: true, disapprove: true, deleteXml: true, deleteReference: true };

export const FULL_PERMS = {
  documents: { read: true, create: true, update: true, delete: true, accounting: true, download: true, ...NEW_FLAGS },
  folders:   { read: true, create: true, update: true, delete: true, closing: true, download: true }
};

export const ADMIN_PERMS = {
  documents: { read: true, create: true, update: true, delete: true, accounting: true, download: true, ...NEW_FLAGS },
  folders:   { read: true, create: true, update: true, delete: true, closing: false, download: true }
};

// deleteLinked final: lo nuevo, o lo viejo combinado con `delete` unido (como hoy).
// El efectivo CONSERVA granular:true: list_folders lo reusa como matriz de las
// subcarpetas sin matriz propia, y sin la marca se releería como viejo.
const finish = (out) => {
  const { _legacyLinked, ...documents } = out.documents;
  documents.deleteLinked = !!documents.deleteLinked || (!!_legacyLinked && !!documents.delete);
  documents.granular = true;
  return { documents, folders: out.folders };
};

/**
 * Lo que la matriz de un admin de empresa sobreescribe de ADMIN_PERMS. Igual
 * que el spread de siempre: solo las claves que la matriz TRAE (una ausente
 * deja la de ADMIN_PERMS). En una matriz vieja, una clave nueva ausente toma
 * su clave de origen si esa sí está.
 */
const adminOverrides = (raw) => {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  GRANULAR_DOC_FLAGS.forEach((f) => { if (f !== 'deleteLinked' && f in raw) out[f] = !!raw[f]; });
  if (raw.granular !== true) {
    Object.entries(INHERIT).forEach(([flag, src]) => {
      if (!(flag in raw) && src in raw) out[flag] = !!raw[src];
    });
  }
  return out;
};

/** LEGADO: motor anterior (rol como piso). No usar en código nuevo. */
export const computeEffective = ({ level, isAdmin, matrix, folderGlobal, globalRole, customerFloor }) => {
  if (level === 'all') return FULL_PERMS;
  if (isAdmin) {
    const raw = matrix?.documents;
    const fls = matrix?.folders || {};
    const documents = { ...ADMIN_PERMS.documents, ...adminOverrides(raw) };
    if (raw && 'deleteLinked' in raw) {
      documents.deleteLinked = raw.granular === true
        ? !!raw.deleteLinked
        : (legacyLinkedRaw(matrix) && !!documents.delete);
    }
    return { documents, folders: { ...ADMIN_PERMS.folders, ...fls, closing: false } };
  }
  const perFolder = mergePermissions(folderGlobal, matrix);
  const withRole  = mergePermissions(globalRole, perFolder);
  return finish(mergePermissions(customerFloor, withRole));
};

const copyPerms = (p) => ({ documents: { ...p.documents }, folders: { ...p.folders } });

// Sin permisos: lo que se entrega mientras una matriz no se pudo leer (R2).
const emptyPerms = () => finish(mergePermissions(null, null));

/** Sin permisos (carpeta restringida, o lectura fallida, R2). */
export const emptyEffective = () => emptyPerms();

// Base del admin de empresa: ADMIN_PERMS con lo que su matriz trae (como el
// computeEffective de siempre), marcada granular para que la unión no la
// vuelva a heredar.
const adminBase = (matrix) => {
  const raw = matrix?.documents;
  const documents = { ...ADMIN_PERMS.documents, ...adminOverrides(raw) };
  if (raw && 'deleteLinked' in raw) {
    documents.deleteLinked = raw.granular === true ? !!raw.deleteLinked : (legacyLinkedRaw(matrix) && !!documents.delete);
  }
  documents.granular = true;
  return { documents, folders: { ...ADMIN_PERMS.folders, ...(matrix?.folders || {}), closing: false } };
};

// Ruling I2: doc de empresa SIN `permissions` (companyMatrixState 'present' y
// companyMatrix null) → base VACÍA para todos menos root; el admin conserva
// ADMIN_PERMS porque su matriz (legado) solo hace overrides. folders.closing
// sale solo del rol (resolveEffective), nunca de matrices ni globales.
const companyBase = ({ kind, role, roleId, isAdmin, companyMatrix, companyMatrixState }) => {
  if (kind === 'admin') return adminBase(companyMatrix);
  if (companyMatrix) return companyMatrix;
  if (companyMatrixState === 'present') return null; // doc sin permissions: nada
  return defaultCompanyPerms({ role, isAdmin, roleId });
};

export const resolveEffective = ({
  role, roleId = role?.id, isRootUser, isAdmin, companyMatrix, companyMatrixState,
} = {}) => {
  const kind = isRootUser === true ? 'root' : userKind({ role, isAdmin, roleId });
  if (kind === 'root') return copyPerms(FULL_PERMS);
  if (companyMatrixState === 'error') return emptyPerms();
  const base = companyBase({ kind, role, roleId, isAdmin, companyMatrix: companyMatrix || null, companyMatrixState });
  const out = finish(mergePermissions(base, null));
  out.folders.closing = kind !== 'admin' && role?.folders?.closing === true;
  return out;
};

export default resolveEffective;
